import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// APOE ε3/ε4 + LIPC + PPARA — the low-fat diet that made everything worse.
// The aha: standard dietary advice is population-averaged. For this genetic profile,
// the prescription is inverted — more fat (the right kinds), fewer refined carbs.

export const apoeSofia: Scenario = {
  slug: 'apoe-sofia',
  series: 'APOE & Brain Health',
  title: "She followed the low-fat plan. The numbers got worse.",
  socialHook:
    "Sofia, 45, cut fat for two years exactly as advised. Her HDL dropped and triglycerides rose. The mystery was not discipline — it was the wrong fuel mix for her body.",

  meta: {
    description:
      "A fictional case study: Sofia has APOE ε3/ε4 and was told to go low-fat. Two years later her HDL fell and triglycerides climbed. LIPC and PPARA explain why the standard advice inverted her results.",
  },

  persona: {
    name: 'Sofia',
    age: 45,
    sex: 'Female',
    ancestry: 'Colombian-British',
    occupation: 'Data analyst',
    chiefConcern:
      "Two years ago Sofia's total cholesterol was 236 mg/dL and LDL was 151 mg/dL. Her family doctor noted her family history and advised a low-fat, higher-carbohydrate diet. She followed it closely. LDL barely moved, HDL dropped from 62 to 43 mg/dL, and triglycerides climbed from 54 to 100 mg/dL. She feels blamed by numbers she worked hard to improve.",
    familyHistory:
      "Father: heart attack at 59, on statins. Paternal aunt: type 2 diabetes. No known Alzheimer's or dementia.",
  },

  clinicalPicture: {
    symptoms: [
      'Persistent fatigue and low energy — worse since the diet change',
      'Brain fog and difficulty concentrating, especially mid-afternoon',
      'Increased hunger and cravings for sweet foods since going low-fat',
      'Mild weight gain over two years despite eating less fat',
    ],
    labs: [
      { name: 'HDL Cholesterol', value: '43 mg/dL', refRange: '>46 mg/dL', flag: 'low' },
      { name: 'Triglycerides', value: '100 mg/dL', refRange: '<66 mg/dL', flag: 'high' },
    ],
    medications: ['No prescription medications — family doctor considering starting a statin'],
    supplements: ['Omega-3 fish oil 1,000mg (started 6 months ago)', 'Vitamin D 1,000 IU'],
    lifestyle: [
      'Diet: strict low-fat for 2 years — <25% calories from fat, high in wholegrains, fruit, low-fat dairy',
      'Exercise: 3x/week gym — mix of cardio and light weights',
      'Alcohol: 4–5 drinks/week (wine with dinner)',
      'Sleep: 7 hours, reasonable quality',
      'Non-smoker',
    ],
  },

  genetics: [
    {
      gene: 'APOE',
      variant: 'ε3/ε4',
      rsid: 'rs429358 / rs7412',
      genotype: 'One copy of ε4',
      plainMeaning:
        "Sofia's result means the type of fat matters more than a simple low-fat rule. APOE ε4 makes saturated fat and LDL important, but it does not mean all fat is the enemy.",
      technicalDetail:
        "APOE ε3/ε4 raises risk of late-onset Alzheimer's 3–4× compared to the most common ε3/ε3 type. ε4 carriers absorb dietary cholesterol more efficiently and their LDL responds more strongly to saturated fat. But this response is highly fat-type specific and interacts with other variants (LIPC, PPARA) that determine whether reducing dietary fat overall helps or hurts.",
    },
    {
      gene: 'LIPC',
      variant: 'rs1800588 (C allele, reduced hepatic lipase activity)',
      rsid: 'rs1800588',
      genotype: 'Reduced hepatic lipase activity',
      plainMeaning:
        "The low-fat plan removed something her HDL may have needed. LIPC helps explain why her protective HDL fell instead of rising.",
      technicalDetail:
        "Hepatic lipase (LIPC) is the enzyme that clears HDL particles from the bloodstream. In most people, reducing dietary fat raises HDL because the enzyme is moderately active. With reduced LIPC activity, HDL clearance is already slow — so fat reduction removes the stimulus that was maintaining HDL levels, causing them to fall. This variant is rarely tested in routine care but completely inverts the standard dietary HDL prescription.",
    },
    {
      gene: 'PPARA',
      variant: 'rs4253728 / rs1800206 (reduced activity)',
      rsid: 'rs4253728',
      genotype: 'Reduced PPAR-alpha transcriptional activity',
      plainMeaning:
        "When Sofia replaced fat with more carbohydrates, her liver turned more of that fuel into triglycerides. PPARA helps explain why the blood fat rose.",
      technicalDetail:
        "PPAR-alpha is the master regulator of fat oxidation in the liver and muscle. A reduced-activity variant means Sofia's cells are less efficient at burning fatty acids. When she increased carbohydrate intake, the liver received more glucose substrate than it could process and converted the excess into triglycerides — driving up her blood triglycerides and insulin resistance. The standard 'heart-healthy' high-carbohydrate prescription was metabolically wrong for her specific genotype.",
    },
    {
      gene: 'FADS2',
      variant: 'rs1535 (reduced delta-6-desaturase activity)',
      rsid: 'rs1535',
      genotype: 'Reduced long-chain fatty acid conversion',
      plainMeaning:
        "Her omega-3 supplement may be the wrong form for her body. FADS2 helps explain why she may need pre-formed EPA and DHA.",
      technicalDetail:
        "FADS2 encodes the enzyme that converts short-chain omega-3 (ALA, found in most standard fish oil capsules) into long-chain EPA and DHA. A reduced-function variant severely impairs that conversion. Sofia needs pre-formed EPA and DHA — from oily fish, algae oil, or a triglyceride-form fish oil — not the short-chain precursors in standard capsules. This also partly explains why her triglycerides haven't responded to supplementing.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "She followed the plan, and the pattern got stranger",
      body: "Sofia is a data analyst, so she treated cholesterol like a problem to solve: follow the plan, track the outputs, adjust if needed. Her family doctor said reduce fat. She reduced fat. She logged meals, switched from olive oil to cooking spray, moved from full-fat yogurt to 0% fat, and cut eggs. At recheck, her protective HDL had dropped, triglycerides had climbed, and LDL barely moved. The plan was supposed to make the picture cleaner. Instead, it made the clues louder.",
    },
    {
      type: 'narrative',
      heading: "The pattern was pointing away from fat",
      body: "HDL falling while triglycerides rise is not random. It can happen when the liver receives more carbohydrate than it can handle cleanly and turns the surplus into fat in the bloodstream. That is not the usual fingerprint of “too much fat.” It points to the fuel swap itself: less fat, more carbohydrate, and a body that did not respond the way the average diet advice expected.",
    },
    {
      type: 'reveal',
      heading: "The low-fat rule flipped on her",
      body: "The standard logic says cutting dietary fat can raise HDL. Sofia's LIPC variant changes that expectation. Her HDL was not being cleared in the usual way, so removing fat may have removed support for the very number her doctor wanted to protect. The drop was not a character flaw. It was a sign that population-average advice did not fit her metabolism. In a woman with APOE ε4, low HDL is a meaningful cardiovascular and cognitive risk clue.",
    },
    {
      type: 'reveal',
      heading: "Her liver was making the fat she was trying to avoid",
      body: "The rising triglycerides were the second clue. Sofia's PPARA variant points to less efficient fuel handling in the liver. When she replaced fat calories with carbohydrates, more surplus glucose could be turned into triglycerides. The blood fat was not simply coming from fat she ate; some of it was being made from the carbohydrates she switched to. Her FADS2 result adds another mismatch: the omega-3 form she takes may not convert well into the EPA and DHA her body needs.",
    },
    {
      type: 'action',
      heading: "What changes when the target changes",
      items: [
        "Reintroduce healthy fats and cut refined carbohydrates. The goal is not low-fat; it is fewer refined carbs and better fats such as olive oil, avocado, nuts, eggs, and oily fish.",
        "Switch to pre-formed omega-3: algae-derived DHA or triglyceride-form fish oil. These provide EPA/DHA more directly and bypass the FADS2 bottleneck.",
        "Ask for a lipid specialist referral. Sofia's picture needs LDL, HDL, non-HDL cholesterol, and triglycerides read together in the context of APOE ε4 and LIPC.",
        "Add resistance training two to three times a week. This helps early insulin resistance and gives glucose a better place to go after meals.",
        "Try four alcohol-free weeks before the next blood test. Alcohol can drive triglyceride production, and a short trial can show how much it contributes.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "What moves Sofia's picture now",
      caption:
        "Two years of low-fat dieting moved the clues in the wrong direction. This shows why her next plan focuses on refined carbs, healthy fats, EPA/DHA form, and full lipid context.",
      baselineLabel: "APOE ε3/ε4 + LIPC + PPARA — after 2 years low-fat diet",
      baseline: 78,
      levers: [
        {
          label: "Replace refined carbs with healthy fats",
          delta: -20,
          description: "Targets the PPARA-linked triglyceride problem and removes the low-fat HDL suppression from LIPC.",
        },
        {
          label: "Switch to pre-formed EPA/DHA (bypasses FADS2)",
          delta: -12,
          description: "Algae DHA or TG-form fish oil bypasses the FADS2 conversion bottleneck and provides EPA/DHA more directly.",
        },
        {
          label: "Resistance training 2–3x/week",
          delta: -10,
          description: "Directly improves insulin sensitivity in muscle — addresses the HOMA-IR 4.1 reading before it becomes established insulin resistance.",
        },
        {
          label: "LDL addressed with lipid specialist",
          delta: -8,
          description: "With HDL, triglycerides, and non-HDL all in the picture, a specialist can target the full lipid profile rather than just LDL — including whether a statin is appropriate and at what dose.",
        },
        {
          label: "4-week alcohol-free trial",
          delta: -6,
          description: "Alcohol drives triglyceride synthesis directly. With PPARA-reduced fat oxidation already raising her triglycerides, alcohol has an outsized effect. A trial period will quantify its contribution.",
        },
      ],
    },
    {
      type: 'checkin' as const,
      heading: "Sofia's 14-day dietary reset check-in",
      goalLabel: "Reduce refined carbs · reintroduce healthy fats · switch omega-3 · no alcohol",
      caption:
        "Sofia made four changes on day 1: switched from low-fat to Mediterranean-style eating, added olive oil and eggs back in, replaced her fish oil capsule with algae DHA, and cut alcohol for the trial period. The subjective shift — energy, mental clarity, hunger — arrived faster than she expected. The lipid recheck is at 3 months.",
      days: [
        {
          day: 1,
          scores: [
            { label: 'Energy', value: 4 },
            { label: 'Mental clarity', value: 4 },
            { label: 'Hunger / cravings', value: 7 },
          ],
          note: "First day off low-fat. Had eggs with olive oil for breakfast for the first time in two years. Hunger score is high — that means cravings were bad (scale: lower is better here). Old habits.",
        },
        {
          day: 2,
          scores: [
            { label: 'Energy', value: 5 },
            { label: 'Mental clarity', value: 4 },
            { label: 'Hunger / cravings', value: 6 },
          ],
          note: "Noticeably less afternoon energy crash than usual. Cravings still there but manageable.",
        },
        {
          day: 3,
          scores: [
            { label: 'Energy', value: 5 },
            { label: 'Mental clarity', value: 5 },
            { label: 'Hunger / cravings', value: 5 },
          ],
          note: "Clearer this morning. Hunger more stable — not reaching for snacks at 11am for the first time in months.",
        },
        {
          day: 4,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Mental clarity', value: 5 },
            { label: 'Hunger / cravings', value: 4 },
          ],
          note: "Good energy through the afternoon. Had sardines for lunch — took some getting used to.",
        },
        {
          day: 5,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Mental clarity', value: 6 },
            { label: 'Hunger / cravings', value: 3 },
          ],
          note: "Cravings nearly gone. This is the first time in two years I haven't wanted something sweet after dinner.",
          milestone: "Cravings gone",
        },
        {
          day: 6,
          scores: [
            { label: 'Energy', value: 6 },
            { label: 'Mental clarity', value: 6 },
            { label: 'Hunger / cravings', value: 3 },
          ],
          note: "Weekend — usually when I would have wine. Sparkling water instead. Easier than expected.",
        },
        {
          day: 7,
          scores: [
            { label: 'Energy', value: 7 },
            { label: 'Mental clarity', value: 7 },
            { label: 'Hunger / cravings', value: 3 },
          ],
          note: "One week. Energy is the most consistent it has been in months. Feels counterintuitive — I added fat and I feel better.",
        },
        {
          day: 8,
          scores: [
            { label: 'Energy', value: 7 },
            { label: 'Mental clarity', value: 7 },
            { label: 'Hunger / cravings', value: 2 },
          ],
          note: "Brain fog lifting noticeably. Finished a data model at work without losing my thread halfway through.",
        },
        {
          day: 9,
          scores: [
            { label: 'Energy', value: 7 },
            { label: 'Mental clarity', value: 8 },
            { label: 'Hunger / cravings', value: 2 },
          ],
          note: "Added a resistance session at the gym today instead of the usual cardio. Harder but felt good.",
          milestone: "First resistance session",
        },
        {
          day: 10,
          scores: [
            { label: 'Energy', value: 8 },
            { label: 'Mental clarity', value: 8 },
            { label: 'Hunger / cravings', value: 2 },
          ],
          note: "10 days in. I feel like I have been solving the wrong problem for two years.",
        },
        {
          day: 11,
          scores: [
            { label: 'Energy', value: 8 },
            { label: 'Mental clarity', value: 8 },
            { label: 'Hunger / cravings', value: 2 },
          ],
          note: "Consistent. Starting to believe the numbers will reflect this at 3 months.",
        },
        {
          day: 12,
          scores: [
            { label: 'Energy', value: 8 },
            { label: 'Mental clarity', value: 9 },
            { label: 'Hunger / cravings', value: 1 },
          ],
          note: "Best focus day I can remember. Worked through a complex brief without the usual 3pm wall.",
        },
        {
          day: 13,
          scores: [
            { label: 'Energy', value: 8 },
            { label: 'Mental clarity', value: 8 },
            { label: 'Hunger / cravings', value: 2 },
          ],
          note: "Stable. Two resistance sessions this week. Feeling stronger.",
        },
        {
          day: 14,
          scores: [
            { label: 'Energy', value: 9 },
            { label: 'Mental clarity', value: 9 },
            { label: 'Hunger / cravings', value: 1 },
          ],
          note: "Two weeks. I am going to go back to my family doctor with this data and ask for a lipid specialist referral. The genetics gave me the frame. The check-in gave me the evidence to advocate for myself.",
          milestone: "family doctor appointment booked",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'apoe4',
    label: 'Explore your APOE genetics in Coach',
  },
}
